import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { Loader2, X, Languages, ArrowRight } from "lucide-react";
import { toast } from "sonner";

/**
 * LanguageStartModal — Iter81.
 *
 * 3-step onboarding for the Duolingo-style language course:
 *  1. Pick the language you already speak (native).
 *  2. Pick the language you want to learn (target).
 *  3. Pick a level (basic / intermediate / advanced).
 *
 * The picker uses real flags + native names so the choice is
 * unambiguous across locales. Levels are explained with example
 * lesson titles to set expectations.
 *
 * Submit → POST /api/lang/start which either creates a new course or
 * returns the existing one for the same (native, target) pair so the
 * user can never accidentally fork their progress.
 */
export default function LanguageStartModal({ open, onOpenChange, onStarted }) {
  const [step, setStep] = useState(1);
  const [languages, setLanguages] = useState([]);
  const [native, setNative] = useState(null);
  const [target, setTarget] = useState(null);
  const [level, setLevel] = useState("basic");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setStep(1); setNative(null); setTarget(null); setLevel("basic");
      return;
    }
    setLoading(true);
    api.get("/lang/languages")
      .then(({ data }) => setLanguages(data?.languages || []))
      .catch(() => toast.error("Couldn't load languages"))
      .finally(() => setLoading(false));
  }, [open]);

  const submit = async () => {
    if (!native || !target || native === target) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/lang/start", { native, target, level });
      onStarted?.(data.course);
      onOpenChange?.(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start your course");
    } finally {
      setSubmitting(false);
    }
  };

  const next = () => setStep((s) => Math.min(3, s + 1));
  const back = () => setStep((s) => Math.max(1, s - 1));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0B0E14]/95 border-white/10 text-white max-w-md p-0 overflow-hidden"
        data-testid="lang-start-modal"
      >
        <div className="px-5 py-4 border-b border-white/8 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 grid place-items-center rounded-xl bg-[#00FF9D]/12 border border-[#00FF9D]/35">
              <Languages size={15} className="text-[#9bffcc]" />
            </div>
            <div>
              <div className="font-display text-sm tracking-tight">Start a language</div>
              <div className="text-[10.5px] text-white/45 font-mono-oak">Step {step} of 3</div>
            </div>
          </div>
          <button
            onClick={() => onOpenChange?.(false)}
            className="h-7 w-7 grid place-items-center rounded-md text-white/45 hover:text-white hover:bg-white/5"
            aria-label="Close"
            data-testid="lang-start-close"
          >
            <X size={14} />
          </button>
        </div>

        {/* Progress bar */}
        <div className="h-1 bg-white/5">
          <motion.div
            className="h-full bg-gradient-to-r from-[#00FF9D] to-[#0044FF]"
            animate={{ width: `${(step / 3) * 100}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>

        <div className="px-5 py-5 min-h-[340px]">
          {loading && (
            <div className="grid place-items-center py-12 text-white/55 text-sm">
              <Loader2 size={16} className="animate-spin mb-2" /> Loading…
            </div>
          )}

          {!loading && step === 1 && (
            <div data-testid="lang-step-native">
              <h3 className="font-display text-lg text-white tracking-tight mb-1">Which language do you speak?</h3>
              <p className="text-[12px] text-white/55 mb-4">We&apos;ll explain everything in this language.</p>
              <LanguageGrid
                languages={languages}
                selected={native}
                onSelect={setNative}
                disabled={null}
                prefix="lang-native"
              />
            </div>
          )}

          {!loading && step === 2 && (
            <div data-testid="lang-step-target">
              <h3 className="font-display text-lg text-white tracking-tight mb-1">Which language do you want to learn?</h3>
              <p className="text-[12px] text-white/55 mb-4">
                You&apos;ll go from zero to confident, one bite-sized lesson at a time.
              </p>
              <LanguageGrid
                languages={languages}
                selected={target}
                onSelect={setTarget}
                disabled={native}
                prefix="lang-target"
              />
            </div>
          )}

          {!loading && step === 3 && (
            <div data-testid="lang-step-level">
              <h3 className="font-display text-lg text-white tracking-tight mb-1">Where do you want to start?</h3>
              <p className="text-[12px] text-white/55 mb-4">
                Don&apos;t worry — you can jump units any time.
              </p>
              <div className="space-y-2.5">
                {[
                  { id: "basic", label: "Basic", body: "Hello, numbers, family, food. Perfect if you've never spoken this language." },
                  { id: "intermediate", label: "Intermediate", body: "Past/present tenses, ordering at a café, travel & directions. You know some vocab." },
                  { id: "advanced", label: "Advanced", body: "Idioms, news, business talk, debating. You're fluent and want to polish." },
                ].map((opt) => {
                  const sel = level === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setLevel(opt.id)}
                      className={`w-full text-left px-3 py-3 rounded-xl border transition ${
                        sel
                          ? "border-[#00FF9D]/55 bg-[#00FF9D]/10"
                          : "border-white/8 bg-white/[0.025] hover:bg-white/[0.05]"
                      }`}
                      data-testid={`lang-level-${opt.id}`}
                    >
                      <div className="font-display text-[14px] text-white">{opt.label}</div>
                      <div className="text-[11.5px] text-white/55 mt-1">{opt.body}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-white/8 flex items-center justify-between gap-2">
          <button
            onClick={back}
            disabled={step === 1 || submitting}
            className="text-[12px] text-white/55 hover:text-white px-3 py-2 rounded-md hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid="lang-start-back"
          >
            Back
          </button>
          {step < 3 && (
            <button
              onClick={next}
              disabled={(step === 1 && !native) || (step === 2 && (!target || target === native))}
              className="btn-primary px-5 py-2 rounded-full font-display font-semibold text-sm disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
              data-testid="lang-start-next"
            >
              Next <ArrowRight size={12} />
            </button>
          )}
          {step === 3 && (
            <button
              onClick={submit}
              disabled={submitting || !native || !target}
              className="btn-primary px-5 py-2 rounded-full font-display font-semibold text-sm disabled:opacity-40 inline-flex items-center gap-1.5"
              data-testid="lang-start-submit"
            >
              {submitting ? <><Loader2 size={12} className="animate-spin" /> Creating…</> : "Start learning"}
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LanguageGrid({ languages, selected, onSelect, disabled, prefix }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-[340px] overflow-y-auto pr-1">
      {languages.map((l) => {
        const sel = selected === l.code;
        const dis = disabled === l.code;
        return (
          <button
            key={l.code}
            type="button"
            disabled={dis}
            onClick={() => onSelect(l.code)}
            className={`text-left px-3 py-3 rounded-xl border flex items-center gap-2 transition ${
              dis
                ? "border-white/5 bg-white/[0.01] text-white/25 cursor-not-allowed"
                : sel
                  ? "border-[#00FF9D]/55 bg-[#00FF9D]/10 text-white"
                  : "border-white/8 bg-white/[0.025] text-white/85 hover:bg-white/[0.05] hover:border-white/20"
            }`}
            data-testid={`${prefix}-${l.code}`}
          >
            <span className="text-xl" aria-hidden>{l.flag}</span>
            <span className="font-display text-[12.5px]">{l.name}</span>
          </button>
        );
      })}
    </div>
  );
}
